import { CommonModule } from '@angular/common';
import { Component, HostListener, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NotificationService } from '../../services/notificationservice';
import { MatSort } from '@angular/material/sort';
import { VGAGasService } from '../../services/vgsgas.service';
import { VGAGasData } from '../../models/vgsgas.model';
import { Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScreenName, LoadScheduleControlFlags } from '../../interfaces/securityPermissions';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { RoleService } from '../../services/role.service';
@Component({
  selector: 'app-vgsgasinput',
  imports: [CommonModule,
    FormsModule,
    MatTableModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDatepickerModule],
  templateUrl: './vgsgasinput.component.html',
  styleUrl: './vgsgasinput.component.scss'
})
export class VgsgasinputComponent {
  selectedDate: Date | null = null;
  responseColumns = [
    'hour', 'mcF_Volume', 'id', 'rngDate'
  ];
  displayedColumns = [
    'hour', 'mcF_Volume'
  ];
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  selectedRowIndex: number | null = null;
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatSort) sort!: MatSort;
  form: any;
  hasAnyChange: boolean = false;
  private destroy$ = new Subject<void>();
  apiResponse: VGAGasData[] = [];

  constructor(private fb: FormBuilder,
    private _vgsGasService: VGAGasService,
    private _notificationService: NotificationService, private rolesService: RoleService,
    private rpService: RolePermissionsService) {
    this.form = this.fb.group({
      rows: this.fb.array([])
    });

    this.selectedDate = new Date();
    this.getVGSGasData(this.selectedDate ? this.selectedDate.toISOString().split('T')[0] : '');

    let roles = this.rolesService.getRolesAsString();
    this.rpService.getRolePermissionsNew(roles, ScreenName.VGS, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }
  private getVGSGasData(date: string) {
    this.clearTable();

    this._vgsGasService.getVGSGasData(date).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: VGAGasData[]) => {
        this.apiResponse = result;
        //Build the dynamic form
        this.apiResponse.forEach(row => {

          const formRow = this.createRow(row);

          //TRACKING CHANGES
          (formRow as any).__original = JSON.stringify(row);
          (formRow as any).__dirty = false;

          formRow.valueChanges.subscribe(() => {
            const isDirty =
              JSON.stringify(formRow.getRawValue()) !==
              (formRow as any).__original;

            (formRow as any).__dirty = isDirty;

            //  GLOBAL FLAG
            this.hasAnyChange = this.rows.controls.some(
              (r: any) => r.__dirty
            );
          });

          this.rows.push(formRow);
        });

        this.hasAnyChange = false;
        this.refreshTable();
      }
    })
  }

  createRow(data?: any) {
    const group: any = {};
    this.responseColumns.forEach(col => {
      if (col === 'id' || col === 'hour') {
        group[col] = [{ value: data?.[col] ?? '', disabled: true }];
      } else {
        group[col] = [{ value: data?.[col] ?? '', disabled: false }];
      }
    });
    return this.fb.group(group);
  }

  clearTable() {
    this.rows.clear();
    this.refreshTable();
    this.hasAnyChange = false;
  }

  get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  refreshTable() {
    this.dataSource.data = this.rows.controls;
    this.dataSource.sort = this.sort;
  }

  /** Excel paste (dynamic columns) */
  @HostListener('document:paste', ['$event'])
  handlePaste(event: ClipboardEvent) {
    if (this.selectedRowIndex === null) return;
    const text = event.clipboardData?.getData('text');
    if (!text) return;

    this.pasteExcel(text);
    event.preventDefault();
  }

  pasteExcel(text: string) {
    const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);
    let index = this.selectedRowIndex ?? 0;

    lines.forEach(line => {
      let row = this.rows.at(index);
      if (!row) {
        row = this.createRow();
        this.rows.push(row);
      }

      const cells = line.split('\t');
      this.responseColumns.forEach((col, i) => {
        row!.get(col)?.setValue(cells[i] ?? '');
      });

      index++;
    });

    this.refreshTable();
  }

  getChangedRows() {
    return this.rows.controls
      .filter((row: any) => row.__dirty)
      .map(row => {
        const payload: any = {};

        this.responseColumns.forEach(col => {
          let value = row.getRawValue()[col];

          payload[col] =
            col === 'hour'
              ? value === '' ? null : Number(value)
              : value;
        });

        return payload;
      });
  }
  
  selectRow(i: number) {
    this.selectedRowIndex = i;
  }

  getPayload() {
    return this.rows.getRawValue().map(row => {
      const payload: any = {};

      this.responseColumns.forEach(col => {
        let value = row[col];

        if (col === 'id') {
          payload[col] =
            value === '' || value === null ? null : Number(value);
        } else {
          payload[col] = value;
        }
      });

      return payload;
    });
  }
  
  numbersOnly(event: KeyboardEvent) {
    if (event.key < '0' || event.key > '9') {
      event.preventDefault();
    }
  }

  onUpdate() {
    const payload = this.getChangedRows();
    this._vgsGasService.saveVGSGasData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: boolean) => {
          if (result)
            this._notificationService.show('Data Saved Successfully!', 'success');
        }
      });
  }
  onSearch() {
    if (!this.selectedDate) {
      this._notificationService.show('Please select a date', 'warning');
      return;
    }
    this.getVGSGasData(this.selectedDate.toISOString().split('T')[0]);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  }

  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
